import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef } from '@angular/core';
import * as cronParser from 'cron-parser';
import cronstrue from 'cronstrue';
import {COMMON_FORM_IMPORTS} from "../../../shared/import-groups/Common";
import {MatHint} from "@angular/material/form-field";
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatSelectTrigger} from "@angular/material/select";
import { CommonModule, DatePipe } from "@angular/common";

interface BasicSchedule {
    type: 'hourly' | 'daily' | 'weekly' | 'monthly';
    hoursList: string[];
    dayOfWeekList: number[];
    dayOfMonthList: string[];
}

interface PresetSchedule {
    label: string;
    cron: string;
    description: string;
    category: 'common' | 'business' | 'maintenance';
}

@Component({
    selector: 'schedule-picker',
    templateUrl: './schedule-picker.component.html',
    styleUrls: ['./schedule-picker.component.less'],
    imports: [
        COMMON_FORM_IMPORTS,
        MatHint,
        CommonModule,
        MatIconButton,
        MatIcon,
        MatSelectTrigger,
        DatePipe
    ],
    animations: []
})
export class SchedulePickerComponent implements OnInit {
    @Input() schedule: string = '0 * * * *';
    @Input() startExpanded: boolean = false;
    @Output() scheduleChange = new EventEmitter<string>();

    expanded = false;

    basicSchedule: BasicSchedule = {
        type: 'hourly',
        hoursList: ['9'],
        dayOfWeekList: [1],
        dayOfMonthList: ['1']
    };

    cronExpression = '0 * * * *';

    // Human-readable description
    scheduleDescription = '';
    nextRunTimes: Date[] = [];
    nextRunDisplay = '';


    // Options for dropdowns

    hourOptions: any[] = [];
    dayOfWeekOptions = [
        { value: 0, label: 'Sunday' },
        { value: 1, label: 'Monday' },
        { value: 2, label: 'Tuesday' },
        { value: 3, label: 'Wednesday' },
        { value: 4, label: 'Thursday' },
        { value: 5, label: 'Friday' },
        { value: 6, label: 'Saturday' },
    ];

    dayOfMonthOptions: any[] = [];

    // Preset schedules
    presetSchedules: PresetSchedule[] = [
        // Common
        { label: 'Every hour', cron: '0 * * * *', description: 'Runs at the top of every hour', category: 'common' },
        { label: 'Daily at midnight', cron: '0 0 * * *', description: 'Runs every day at midnight', category: 'common' },
        { label: 'Daily at 9 AM', cron: '0 9 * * *', description: 'Runs every day at 9:00 AM', category: 'common' },
        { label: 'Weekly on Monday', cron: '0 9 * * 1', description: 'Runs every Monday at 9:00 AM', category: 'common' },

        // Business
        { label: 'Business hours', cron: '0 9-17 * * 1-5', description: 'Every hour from 9 AM to 5 PM on weekdays', category: 'business' },
        { label: 'Twice daily', cron: '0 9,17 * * *', description: 'At 9:00 AM and 5:00 PM every day', category: 'business' },
        { label: 'Start of business', cron: '0 9 * * 1-5', description: 'At 9:00 AM on weekdays', category: 'business' },

        // Maintenance
        { label: 'End of day', cron: '0 18 * * *', description: 'Every day at 6:00 PM', category: 'maintenance' },
        { label: 'End of week', cron: '0 18 * * 5', description: 'Every Friday at 6:00 PM', category: 'maintenance' },
        { label: 'End of month', cron: '0 23 L * *', description: 'Last day of month at 11:00 PM', category: 'maintenance' },
        { label: 'Weekend nights', cron: '0 2 * * 0,6', description: 'At 2:00 AM on weekends', category: 'maintenance' }
    ];


    constructor(private cdr: ChangeDetectorRef) {}

    ngOnInit() {
        this.expanded = this.startExpanded;

        // Initialize hour options
        for (let i = 0; i < 24; i++) {
            const hour12 = i === 0 ? 12 : (i > 12 ? i - 12 : i);
            const period = i < 12 ? 'AM' : 'PM';
            this.hourOptions.push({
                value: i.toString(),
                label: `${hour12}:00 ${period}`
            });
        }

        // Initialize day of month options
        this.dayOfMonthOptions.push({ value: '1', label: '1st' });
        for (let i = 2; i <= 31; i++) {
            this.dayOfMonthOptions.push({
                value: i.toString(),
                label: i.toString()
            });
        }
        this.dayOfMonthOptions.push({ value: 'L', label: 'Last day' });

        // Update preset descriptions using cronstrue
        this.presetSchedules.forEach(preset => {
            try {
                preset.description = cronstrue.toString(preset.cron, {
                    use24HourTimeFormat: false,
                    dayOfWeekStartIndexZero: true,
                    verbose: true
                }) + ' UTC';
            } catch (e) {
                // Keep original description if cronstrue fails
            }
        });

        // Initialize from input
        if (this.schedule) {
            this.cronExpression = this.schedule;
            this.parseScheduleToBasic();
        }
        this.updateDescription();
    }

    toggleExpanded() {
        this.expanded = !this.expanded;
        this.cdr.detectChanges();
    }


    onScheduleChange() {
        this.updateCronFromBasic();
        this.updateDescription();
        this.emitChange();
    }


    applyPreset(preset: PresetSchedule) {
        this.cronExpression = preset.cron;
        this.parseScheduleToBasic();
        this.updateDescription();
        this.emitChange();
    }


    private updateCronFromBasic() {
        // Always force minute to 0 per backend constraint
        const minute = '0';
        let hour: string;
        let dayOfWeek: string = '*';
        let dayOfMonth: string = '*';

        // Helper function to convert array of numbers to range string
        const toRangeString = (numbers: number[]): string => {
            if (numbers.length === 0) return '*';

            const sorted = [...numbers].sort((a, b) => a - b);
            const ranges: string[] = [];
            let start = sorted[0];
            let end = sorted[0];

            for (let i = 1; i <= sorted.length; i++) {
                if (i < sorted.length && sorted[i] === end + 1) {
                    // Continue the range
                    end = sorted[i];
                } else {
                    // End of range or array
                    if (start === end) {
                        ranges.push(start.toString());
                    } else if (end === start + 1) {
                        // Only 2 consecutive numbers, list them separately
                        ranges.push(start.toString());
                        ranges.push(end.toString());
                    } else {
                        ranges.push(`${start}-${end}`);
                    }

                    if (i < sorted.length) {
                        start = sorted[i];
                        end = sorted[i];
                    }
                }
            }

            return ranges.join(',');
        };

        // Handle hour field - if all 24 hours selected or none, use *
        if (this.basicSchedule.hoursList.length === 0 || this.basicSchedule.hoursList.length === 24) {
            hour = '*';
        } else {
            hour = toRangeString(this.basicSchedule.hoursList.map(h => parseInt(h)));
        }

        // Handle day of week for weekly schedules
        if (this.basicSchedule.type === 'weekly') {
            if (this.basicSchedule.dayOfWeekList.length === 0 || this.basicSchedule.dayOfWeekList.length === 7) {
                dayOfWeek = '*';
            } else {
                dayOfWeek = toRangeString(this.basicSchedule.dayOfWeekList);
            }
        }

        // Handle day of month for monthly schedules
        if (this.basicSchedule.type === 'monthly') {
            // 32 because we have 31 days + 'L' for last day
            if (this.basicSchedule.dayOfMonthList.length === 0 || this.basicSchedule.dayOfMonthList.length >= 31) {
                dayOfMonth = '*';
            } else {
                // Separate numeric days from 'L'
                const hasL = this.basicSchedule.dayOfMonthList.includes('L');
                const numericDays = this.basicSchedule.dayOfMonthList
                    .filter(d => d !== 'L')
                    .map(d => parseInt(d));

                const numericPart = numericDays.length > 0 ? toRangeString(numericDays) : '';

                if (hasL) {
                    dayOfMonth = numericPart ? `${numericPart},L` : 'L';
                } else {
                    dayOfMonth = numericPart;
                }
            }
        }

        // Build cron expression based on type
        switch (this.basicSchedule.type) {
            case 'hourly':
                this.cronExpression = `0 * * * *`;
                break;
            case 'daily':
                this.cronExpression = `0 ${hour} * * *`;
                break;
            case 'weekly':
                this.cronExpression = `0 ${hour} * * ${dayOfWeek}`;
                break;
            case 'monthly':
                this.cronExpression = `0 ${hour} ${dayOfMonth} * *`;
                break;
        }
    }

    private parseScheduleToBasic() {
        try {
            const parts = this.cronExpression.split(' ');
            if (parts.length !== 5) return;

            const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

            // Reset lists
            this.basicSchedule.hoursList = [];
            this.basicSchedule.dayOfWeekList = [];
            this.basicSchedule.dayOfMonthList = [];

            // Helper to expand ranges like "9-17" to [9,10,11,...,17]
            const expandRange = (range: string): string[] => {
                const [start, end] = range.split('-').map(n => parseInt(n));
                const result = [];
                for (let i = start; i <= end; i++) {
                    result.push(i.toString());
                }
                return result;
            };

            // Parse hour field
            if (hour === '*') {
                // Leave empty, will be treated as all hours
                this.basicSchedule.hoursList = [];
            } else if (hour.includes(',')) {
                // Handle comma-separated values, including ranges
                const hourParts = hour.split(',');
                for (const part of hourParts) {
                    if (part.includes('-')) {
                        this.basicSchedule.hoursList.push(...expandRange(part));
                    } else {
                        this.basicSchedule.hoursList.push(part);
                    }
                }
            } else if (hour.includes('-')) {
                this.basicSchedule.hoursList = expandRange(hour);
            } else {
                this.basicSchedule.hoursList = [hour];
            }

            // Parse day of week field
            if (dayOfWeek !== '*') {
                if (dayOfWeek.includes(',')) {
                    const dayParts = dayOfWeek.split(',');
                    for (const part of dayParts) {
                        if (part.includes('-')) {
                            const [start, end] = part.split('-').map(n => parseInt(n));
                            for (let i = start; i <= end; i++) {
                                this.basicSchedule.dayOfWeekList.push(i);
                            }
                        } else {
                            this.basicSchedule.dayOfWeekList.push(parseInt(part));
                        }
                    }
                } else if (dayOfWeek.includes('-')) {
                    const [start, end] = dayOfWeek.split('-').map(n => parseInt(n));
                    for (let i = start; i <= end; i++) {
                        this.basicSchedule.dayOfWeekList.push(i);
                    }
                } else {
                    this.basicSchedule.dayOfWeekList = [parseInt(dayOfWeek)];
                }
            }

            // Parse day of month field
            if (dayOfMonth !== '*') {
                if (dayOfMonth.includes(',')) {
                    this.basicSchedule.dayOfMonthList = dayOfMonth.split(',');
                } else {
                    this.basicSchedule.dayOfMonthList = [dayOfMonth];
                }
            }

            // Detect schedule type
            if (dayOfWeek !== '*') {
                this.basicSchedule.type = 'weekly';
            } else if (dayOfMonth !== '*') {
                this.basicSchedule.type = 'monthly';
            } else if (hour !== '*') {
                this.basicSchedule.type = 'daily';
            } else {
                this.basicSchedule.type = 'hourly';
            }

            // Remove duplicates and sort
            this.basicSchedule.hoursList = [...new Set(this.basicSchedule.hoursList)]
                .sort((a, b) => parseInt(a) - parseInt(b));
            this.basicSchedule.dayOfWeekList = [...new Set(this.basicSchedule.dayOfWeekList)]
                .sort((a, b) => a - b);
            this.basicSchedule.dayOfMonthList = [...new Set(this.basicSchedule.dayOfMonthList)]
                .sort((a, b) => {
                    if (a === 'L') return 1;
                    if (b === 'L') return -1;
                    return parseInt(a) - parseInt(b);
                });

        } catch (e) {
            console.error('Error parsing cron expression:', e);
        }
    }

    private updateDescription() {
        try {
            // Parse cron expression in UTC timezone
            const interval = cronParser.parseExpression(this.cronExpression, {
                tz: 'UTC'
            });
            this.scheduleDescription = this.getHumanReadable(this.cronExpression);

            // Calculate next 5 run times in UTC
            this.nextRunTimes = [];
            for (let i = 0; i < 5; i++) {
                this.nextRunTimes.push(interval.next().toDate());
            }
            this.updateNextRunDisplay();
        } catch (e: any) {
            this.scheduleDescription = 'Invalid expression';
            this.nextRunTimes = [];
            this.nextRunDisplay = '';
        }
    }

    private updateNextRunDisplay() {
        if (this.nextRunTimes.length > 0) {
            const next = this.nextRunTimes[0];
            this.nextRunDisplay = `Next run: ${this.formatDateTime(next)}`;
        }
    }

    private formatDateTime(date: Date): string {
        // Format in local time with timezone info
        const options: Intl.DateTimeFormatOptions = {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        };
        const localTime = date.toLocaleString('en-US', options);
        const tzAbbr = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
            .formatToParts(date)
            .find(part => part.type === 'timeZoneName')?.value || '';
        return `${localTime} ${tzAbbr}`;
    }

    private getHumanReadable(cron: string): string {
        try {
            // Use cronstrue to generate human-readable description
            // Options: use24HourTimeFormat: false for 12-hour format with AM/PM
            // dayOfWeekStartIndexZero: true to match cron standard (0=Sunday)
            // verbose: true for more detailed descriptions
            const description = cronstrue.toString(cron, {
                use24HourTimeFormat: false,
                dayOfWeekStartIndexZero: true,
                verbose: true
            });

            // Add UTC suffix since we're using UTC timezone
            return description + ' UTC';
        } catch (error) {
            // Fallback to the cron expression if cronstrue fails
            console.warn('Failed to parse cron expression with cronstrue:', error);
            return cron;
        }
    }

    private emitChange() {
        this.scheduleChange.emit(this.cronExpression);
    }

    get commonPresets(): PresetSchedule[] {
        return this.presetSchedules.filter(p => p.category === 'common');
    }

    get businessPresets(): PresetSchedule[] {
        return this.presetSchedules.filter(p => p.category === 'business');
    }

    get maintenancePresets(): PresetSchedule[] {
        return this.presetSchedules.filter(p => p.category === 'maintenance');
    }
}
